import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import { confirmDialog } from "../components/ConfirmDialog";
import {
  Dashboard6Styles, ModalShell, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader, KpiStrip, Pagination,
} from "../components/ModalUI";

// ==========================================
// CONSTANTS & STORAGE HELPERS
// ==========================================
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";
const PAGE_SIZE = 200;

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() {
  return getSession()?.accessToken || "";
}

export function getRefreshToken() {
  return getSession()?.refreshToken || "";
}

export function saveLoginSession(payload) {
  setSession(payload);
  localStorage.setItem("auth_token", payload?.accessToken || "");
}

// ==========================================
// HTTP & NETWORK UTILITIES
// ==========================================
async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

// ==========================================
// FORMATTING & AVATAR HELPERS
// ==========================================
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(iso);
  }
}

function getInitials(name = "") {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

const AVATAR_COLORS = [
  "bg-light-primary text-primary",
  "bg-light-success text-success",
  "bg-light-warning text-warning",
  "bg-light-danger text-danger",
  "bg-light-info text-info",
];

async function uploadAdminImage(imageFile, adminId) {
  if (!imageFile || !adminId) return null;
  try {
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, {
      method: "POST",
      headers: {},
      body: formData,
    });
    const json = safeJsonParse(await res.text());
    if (!res.ok || typeof json?.data !== "string" || !json.data.startsWith("http")) return null;
    return json.data;
  } catch {
    return null;
  }
}

const PHASE_ADMIN = "Phase Admin";

function parseList(json) {
  const raw = json?.data;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  return [];
}

// The phases a phase administrator currently manages (looked up from the phases list).
function findAssignedPhaseIds(phases, userId) {
  return phases
    .filter((ph) => {
      const adminRef = ph.phaseAdministratorId ?? ph.administratorId ?? ph.phaseAdministrator?.id;
      return adminRef && String(adminRef) === String(userId);
    })
    .map((ph) => ph.id);
}

function PhaseChecklist({ phases, selected, onToggle, disabled }) {
  if (!phases.length) return <div className="d6m-muted">No phases have been created for this estate yet.</div>;
  return (
    <div className="d6m-list">
      {phases.map((ph) => {
        const checked = selected.includes(ph.id);
        return (
          <label key={ph.id} className={`d6m-list-row d6m-check-row ${checked ? "active" : ""}`}>
            <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onToggle(ph.id)} />
            <span className="d6m-check" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
            </span>
            <span className="min-w-0 flex-grow-1"><span className="d6m-v d-block">{ph.name}</span></span>
          </label>
        );
      })}
    </div>
  );
}

// ==========================================
// MODAL COMPONENTS
// ==========================================
function AddUserModal({ modalRef, estateId, adminId, roleTarget, phases, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);

  const [phaseIds, setPhaseIds] = useState([]);
  // An "Admin" with phases ticked is created as a phase administrator limited to those phases.
  const isPhaseAdmin = roleTarget === "Admin" && phaseIds.length > 0;
  const togglePhase = (id) => setPhaseIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const blankForm = { title: "", firstName: "", lastName: "", email: "", phoneNumber: "", password: "password", confirmPassword: "password" };
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    const first = form.firstName.trim().toLowerCase().replace(/\s+/g, "");
    const last = form.lastName.trim().toLowerCase().replace(/\s+/g, "");
    if (first || last) {
      setForm((p) => ({ ...p, email: `${first}${last}@zangon.com` }));
    } else {
      setForm((p) => ({ ...p, email: "" }));
    }
  }, [form.firstName, form.lastName]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.phoneNumber.trim()) e.phoneNumber = "Required";
    if (!form.password || form.password.length < 6) e.password = "Min 6 characters";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetAndClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    setErrors({});
    setImagePreview(null);
    setImageFile(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPhaseIds([]);
    setForm(blankForm);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let imageUrl = null;
      if (imageFile && adminId && roleTarget === "Security" && !isPhaseAdmin) {
        setUploadingImage(true);
        imageUrl = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
      }

      const payload = {
        ...(isPhaseAdmin ? { title: form.title.trim() } : { estateId }),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        password: form.password,
        confirmPassword: form.confirmPassword,
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(imageUrl && { imageUrl }),
      };

      const res =
        isPhaseAdmin
          ? await authFetch(`${API_BASE}/estates/${estateId}/phase-administrators`, { method: "POST", body: JSON.stringify(payload) })
          : roleTarget === "Admin"
          ? await authFetch(`${API_BASE}/auth/register/admin`, { method: "POST", body: JSON.stringify(payload) })
          : await authFetch(`${API_BASE}/estates/${estateId}/security-personnel`, { method: "POST", body: JSON.stringify(payload) });

      const textResponse = await res.text();
      const json = safeJsonParse(textResponse);

      if (!res.ok) {
        throw new Error(json?.message || `Failed to create ${roleTarget.toLowerCase()}.`);
      }

      if (isPhaseAdmin) {
        let createdId = json?.data?.id || json?.data?.phaseAdministratorId || (typeof json?.data === "string" ? json.data : null);
        if (!createdId) {
          const listRes = await authFetch(`${API_BASE}/estates/${estateId}/phase-administrators`);
          const match = parseList(safeJsonParse(await listRes.text())).find((a) => a.phoneNumber === payload.phoneNumber);
          createdId = match?.id || match?.phaseAdministratorId;
        }
        if (!createdId) throw new Error("Phase admin created, but couldn't assign phases. Assign them from the edit screen.");
        const results = await Promise.all(
          phaseIds.map((phaseId) =>
            authFetch(`${API_BASE}/estates/${estateId}/phases/${phaseId}/phase-administrator/${createdId}`, { method: "POST", body: "{}" }).then((r) => r.ok).catch(() => false)
          )
        );
        if (results.some((ok) => !ok)) {
          toast.error("Phase admin created, but some phases couldn't be assigned. Check the edit screen.");
        }
      } else if (imageFile && roleTarget === "Admin") {
        try {
          const createdId = json?.data?.id || json?.data?.adminId || json?.data;
          if (createdId) {
            const fd = new FormData();
            fd.append("file", imageFile);
            await authFetch(`${API_BASE}/admins/${createdId}/uploads`, { method: "POST", headers: {}, body: fd });
          }
        } catch {
          toast("User created, but image upload failed.", { icon: "⚠️" });
        }
      }

      toast.success(`${form.firstName} registered as ${isPhaseAdmin ? PHASE_ADMIN : roleTarget} successfully!`);
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const inputCls = (key) => `d6m-input ${errors[key] ? "is-invalid" : ""}`;
  const busy = loading || uploadingImage;

  return (
    <ModalShell
      modalRef={modalRef}
      icon={roleTarget === "Security" ? "ki-shield-tick" : "ki-setting-2"}
      title={`Register ${isPhaseAdmin ? PHASE_ADMIN : roleTarget}`}
      onClose={resetAndClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={busy}>
            {uploadingImage ? (
              <><span className="spinner-border spinner-border-sm me-2" />Uploading photo…</>
            ) : loading ? (
              <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
            ) : (
              `Create ${roleTarget}`
            )}
          </button>
        </>
      }
    >
      {!isPhaseAdmin && <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">Profile photo</div>
          <div className="d6m-muted">Optional · PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change" : "Upload"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>}

      <div className="d6m-section">
        <div className="d6m-section-head"><span>User details</span></div>
        <div className="row g-4">
          {isPhaseAdmin && (
            <Field className="col-12" label="Title" hint="e.g. Mr, Mrs, Dr, Chief">
              <input className="d6m-input" placeholder="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
            </Field>
          )}
          <Field className="col-6" label="First name" required error={errors.firstName}>
            <input className={inputCls("firstName")} placeholder="First name" value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} />
          </Field>
          <Field className="col-6" label="Last name" required error={errors.lastName}>
            <input className={inputCls("lastName")} placeholder="Last name" value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} />
          </Field>
          <Field className="col-12" label="Phone number" required error={errors.phoneNumber}>
            <input className={inputCls("phoneNumber")} placeholder="Phone number" value={form.phoneNumber} onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))} />
          </Field>
          <Field className="col-12" label="Email" hint="Generated from their name.">
            <input type="email" className="d6m-input is-readonly" value={form.email} readOnly />
          </Field>
        </div>
      </div>

      {roleTarget === "Admin" && (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Phase access</span></div>
          <div className="d6m-muted mb-3">Tick the phases this admin can see. Leave all unticked for an estate-wide admin.</div>
          <PhaseChecklist phases={phases || []} selected={phaseIds} onToggle={togglePhase} disabled={busy} />
        </div>
      )}

      <div className="d6m-section">
        <div className="d6m-section-head"><span>Account security</span></div>
        <div className="row g-4">
          <Field className="col-12" label="Password" required error={errors.password}>
            <div className="position-relative">
              <input
                type={showPassword ? "text" : "password"}
                className={inputCls("password")}
                placeholder="Minimum 6 characters"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                style={{ paddingRight: "2.75rem" }}
              />
              <button type="button" className="btn btn-icon btn-sm position-absolute end-0 top-0 h-100 text-gray-500" onClick={() => setShowPassword(!showPassword)}>
                <i className={`ki-outline ki-eye${showPassword ? "-slash" : ""} fs-4`} />
              </button>
            </div>
          </Field>
          <Field className="col-12" label="Confirm password" required error={errors.confirmPassword}>
            <div className="position-relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={inputCls("confirmPassword")}
                placeholder="Confirm password"
                value={form.confirmPassword}
                onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                style={{ paddingRight: "2.75rem" }}
              />
              <button type="button" className="btn btn-icon btn-sm position-absolute end-0 top-0 h-100 text-gray-500" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                <i className={`ki-outline ki-eye${showConfirmPassword ? "-slash" : ""} fs-4`} />
              </button>
            </div>
          </Field>
        </div>
      </div>
    </ModalShell>
  );
}

function EditUserModal({ modalRef, user, estateId, adminId, phases, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ title: "", firstName: "", lastName: "", email: "", phoneNumber: "", active: true, phaseIds: [] });
  const isPhaseAdmin = user?.role === PHASE_ADMIN;
  const originalPhaseIds = useMemo(() => (user && isPhaseAdmin ? findAssignedPhaseIds(phases || [], user.id) : []), [user, isPhaseAdmin, phases]);

  useEffect(() => {
    if (user) {
      setForm({
        title: user.title || "",
        phaseIds: originalPhaseIds,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        active: user.isActive ?? true,
      });
      setImagePreview(user.imageUrl || null);
      setImageFile(null);

      // Phase admins: refresh the form from GET /phase-administrators/{id} so edits start from current server data.
      if (isPhaseAdmin && estateId && user.id) {
        let cancelled = false;
        (async () => {
          try {
            const res = await authFetch(`${API_BASE}/estates/${estateId}/phase-administrators/${user.id}`);
            if (!res.ok) return;
            const json = safeJsonParse(await res.text());
            const d = json?.data ?? json;
            if (cancelled || !d || typeof d !== "object") return;
            setForm((f) => ({
              ...f,
              title: d.title ?? f.title,
              firstName: d.firstName ?? f.firstName,
              lastName: d.lastName ?? f.lastName,
              email: d.email ?? f.email,
              phoneNumber: d.phoneNumber ?? f.phoneNumber,
              active: d.isActive ?? f.active,
            }));
          } catch {
            // Fall back to the values already taken from the list.
          }
        })();
        return () => { cancelled = true; };
      }
    }
    return undefined;
  }, [user, originalPhaseIds, isPhaseAdmin, estateId]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetAndClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim()) return toast.error("First name is required.");
    if (!form.lastName.trim()) return toast.error("Last name is required.");
    if (!form.phoneNumber.trim()) return toast.error("Phone number is required.");
    setSaving(true);
    try {
      let imageUrl = user?.imageUrl || undefined;
      if (imageFile && adminId) {
        setUploadingImage(true);
        const uploaded = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
        if (uploaded) imageUrl = uploaded;
        else toast("Photo upload failed — other changes will still be saved.", { icon: "⚠️" });
      }

      const payload = {
        ...(isPhaseAdmin ? { title: form.title.trim() } : {}),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        isActive: form.active,
        ...(imageUrl && !isPhaseAdmin && { imageUrl }),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
      };

      const endpoint = isPhaseAdmin
        ? `${API_BASE}/estates/${estateId}/phase-administrators/${user.id}`
        : user?.role === "Admin"
        ? `${API_BASE}/admins/${user.id}`
        : `${API_BASE}/estates/${estateId}/security-personnel/${user.id}`;

      const res = await authFetch(endpoint, { method: "PUT", body: JSON.stringify(payload) });
      if (!res.ok) {
        const json = safeJsonParse(await res.text());
        throw new Error(json?.message || "Update failed.");
      }

      if (isPhaseAdmin) {
        const removed = originalPhaseIds.filter((id) => !form.phaseIds.includes(id));
        const added = form.phaseIds.filter((id) => !originalPhaseIds.includes(id));
        const calls = [
          ...removed.map((id) => authFetch(`${API_BASE}/estates/${estateId}/phases/${id}/phase-administrator`, { method: "DELETE" })),
          ...added.map((id) => authFetch(`${API_BASE}/estates/${estateId}/phases/${id}/phase-administrator/${user.id}`, { method: "POST", body: "{}" })),
        ];
        // Each entry is null on success, or the reason the phase change failed.
        const results = await Promise.all(
          calls.map((c) =>
            c
              .then(async (r) => (r.ok ? null : safeJsonParse(await r.text())?.message || `HTTP ${r.status}`))
              .catch((e) => e?.message || "Network error")
          )
        );
        const failed = results.filter(Boolean);
        if (failed.length) throw new Error(`Details saved, but some phase changes failed: ${failed[0]}`);
      }
      toast.success("User updated successfully.");
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirmDialog({ title: "Delete Phase Admin", message: `Delete ${user.firstName} ${user.lastName}? This cannot be undone.`, confirmLabel: "Delete" }))) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/phase-administrators/${user.id}`, { method: "DELETE" });
      if (!res.ok) {
        const json = safeJsonParse(await res.text());
        throw new Error(json?.message || "Delete failed.");
      }
      toast.success("Phase admin deleted.");
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || uploadingImage;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-profile-user"
      title="Edit Account"
      onClose={resetAndClose}
      closeDisabled={busy}
      footer={
        <>
          {isPhaseAdmin && (
            <button type="button" className="btn btn-light-danger d6-mbtn me-auto" onClick={handleDelete} disabled={busy}>Delete</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={busy}>
            {uploadingImage ? (
              <><span className="spinner-border spinner-border-sm me-2" />Uploading photo…</>
            ) : saving ? (
              <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
            ) : (
              "Save Changes"
            )}
          </button>
        </>
      }
    >
      {!isPhaseAdmin && <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">Profile photo</div>
          <div className="d6m-muted">PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imageFile && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(user?.imageUrl || null); }}>Cancel new photo</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change" : "Upload"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>}

      <div className="d6m-section">
        <div className="d6m-section-head"><span>User details</span></div>
        <div className="row g-4">
          {isPhaseAdmin && (
            <Field className="col-12" label="Title">
              <input className="d6m-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
          )}
          <Field className="col-6" label="First name">
            <input className="d6m-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </Field>
          <Field className="col-6" label="Last name">
            <input className="d6m-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </Field>
          <Field className="col-12" label="Phone number">
            <input className="d6m-input" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} />
          </Field>
          <Field className="col-12" label="Email address">
            <input type="email" className="d6m-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
        </div>
      </div>

      {isPhaseAdmin && (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Phase access</span></div>
          <PhaseChecklist
            phases={phases || []}
            selected={form.phaseIds}
            disabled={busy}
            onToggle={(id) => setForm((f) => ({ ...f, phaseIds: f.phaseIds.includes(id) ? f.phaseIds.filter((x) => x !== id) : [...f.phaseIds, id] }))}
          />
        </div>
      )}

      <div className="d6m-section">
        <div className="d6m-list">
          <label className={`d6m-list-row d6m-check-row ${form.active ? "active" : ""}`}>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span className="d6m-check" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
            </span>
            <span className="min-w-0 flex-grow-1">
              <span className="d6m-v d-block">Account active</span>
              <span className="d6m-muted d-block">Disabled accounts can no longer sign in.</span>
            </span>
          </label>
        </div>
      </div>
    </ModalShell>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function UserManagement() {
  const session = getSession();
  const estateId = session?.estateId || "";
  const adminId = session?.userId || "";

  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = useState(1);

  const [addRoleTarget, setAddRoleTarget] = useState("Security");
  const [selectedUser, setSelectedUser] = useState(null);

  const addModalRef = useRef(null);
  const editModalRef = useRef(null);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const onClick = (e) => {
      const el = e.target.closest('[data-kt-element="mode"]');
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else {
          html.setAttribute("data-bs-theme", mode);
          localStorage.setItem("kt_theme_mode", mode);
        }
      }
    };
    document.addEventListener("click", onClick);
    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => {
      document.removeEventListener("click", onClick);
      clearTimeout(t);
    };
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!estateId) {
      setIsLoading(false);
      setPageReady(true);
      return;
    }

    setIsLoading(true);
    try {
      const [secRes, adminRes, phaseAdminRes, phasesRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/security-personnel?pageIndex=0&pageSize=30`),
        authFetch(`${API_BASE}/estates/${estateId}/admins?pageIndex=0&pageSize=30`),
        authFetch(`${API_BASE}/estates/${estateId}/phase-administrators`).catch(() => null),
        authFetch(`${API_BASE}/estates/${estateId}/phases`).catch(() => null),
      ]);

      const phaseAdminItems = phaseAdminRes?.ok ? parseList(await phaseAdminRes.json()) : [];
      const phaseItems = phasesRes?.ok ? parseList(await phasesRes.json()) : [];
      setPhases(phaseItems);

      let secItems = [];
      if (secRes.ok) {
        const json = await secRes.json();
        secItems = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
      }

      let adminItems = [];
      if (adminRes.ok) {
        const json = await adminRes.json();
        adminItems = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
      }

      const secUsers = secItems.map((p, index) => {
        const uId = p.id || p.securityId;
        const isMe = String(uId) === String(adminId);
        const rawName = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "Unknown";
        const displayName = isMe ? `${rawName} (You)` : rawName;
        return {
          id: uId,
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          name: displayName,
          initials: getInitials(rawName),
          email: p.email || "",
          phoneNumber: p.phoneNumber || "",
          imageUrl: p.imageUrl || null,
          avatarClass: AVATAR_COLORS[index % AVATAR_COLORS.length],
          isActive: p.isActive ?? true,
          role: "Security",
          createdDate: p.createdDate || p.createdAt,
          createdDateFmt: fmtDate(p.createdDate || p.createdAt),
          isMe,
        };
      });

      const adminUsers = adminItems.map((p, index) => {
        const uId = p.id || p.adminId;
        const isMe = String(uId) === String(adminId);
        const rawName = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "Unknown";
        const displayName = isMe ? `${rawName} (You)` : rawName;
        return {
          id: uId,
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          name: displayName,
          initials: getInitials(rawName),
          email: p.email || "",
          phoneNumber: p.phoneNumber || "",
          imageUrl: p.imageUrl || null,
          avatarClass: AVATAR_COLORS[(index + 3) % AVATAR_COLORS.length],
          isActive: p.isActive ?? true,
          role: p.role || "Admin",
          createdDate: p.createdDate || p.createdAt,
          createdDateFmt: fmtDate(p.createdDate || p.createdAt),
          isMe,
        };
      });

      const phaseAdminUsers = phaseAdminItems.map((p, index) => {
        const uId = p.id || p.phaseAdministratorId;
        const rawName = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "Unknown";
        return {
          id: uId,
          title: p.title || "",
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          name: rawName,
          initials: getInitials(rawName),
          email: p.email || "",
          phoneNumber: p.phoneNumber || "",
          imageUrl: p.imageUrl || null,
          avatarClass: AVATAR_COLORS[(index + 1) % AVATAR_COLORS.length],
          isActive: p.isActive ?? true,
          role: PHASE_ADMIN,
          phaseNames: findAssignedPhaseIds(phaseItems, uId)
            .map((id) => phaseItems.find((ph) => ph.id === id)?.name)
            .filter(Boolean),
          createdDate: p.createdDate || p.createdAt,
          createdDateFmt: fmtDate(p.createdDate || p.createdAt),
          isMe: false,
        };
      });

      const hiddenEmails = new Set(["mr@zangon.com", "sos1@zangon.com"]);

      const allUsers = [...secUsers, ...adminUsers, ...phaseAdminUsers]
        .filter((u) => !hiddenEmails.has(u.email?.trim().toLowerCase()))
        .sort((a, b) => {
          if (a.isMe) return -1;
          if (b.isMe) return 1;
          return new Date(b.createdDate || 0) - new Date(a.createdDate || 0);
        });

      setUsers(allUsers);
    } catch (err) {
      console.error("[fetchUsers] error:", err);
      toast.error("Failed to load users.");
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId, adminId]);

  const fetchedState = useRef({ estateId: null });
  useEffect(() => {
    if (fetchedState.current.estateId === estateId) return;
    fetchedState.current.estateId = estateId;
    fetchUsers();
  }, [fetchUsers, estateId]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const stats = useMemo(
    () => ({
      total: users.length,
      security: users.filter((u) => u.role === "Security").length,
      admins: users.filter((u) => u.role === "Admin").length,
      phaseAdmins: users.filter((u) => u.role === PHASE_ADMIN).length,
      active: users.filter((u) => u.isActive).length,
    }),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !q || u.name.toLowerCase().includes(q) || u.phoneNumber.includes(q) || (u.email && u.email.toLowerCase().includes(q));
      const matchRole = roleFilter === "all" || u.role === roleFilter;
      const matchStatus =
        statusFilter === "all" || (statusFilter === "active" && u.isActive) || (statusFilter === "disabled" && !u.isActive);
      return matchSearch && matchRole && matchStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedUsers = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAddModal = (role) => {
    setAddRoleTarget(role);
    if (window.bootstrap?.Modal && addModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(addModalRef.current, { backdrop: "static" }).show();
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    if (window.bootstrap?.Modal && editModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(editModalRef.current, { backdrop: "static" }).show();
    }
  };

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>
              <PageHeader breadcrumb="User Management" title="System Users">
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => openAddModal("Security")}>
                  <i className="ki-outline ki-shield-tick fs-6 me-sm-2" />
                  <span className="d-none d-sm-inline">Register Security</span>
                </button>
                <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => openAddModal("Admin")}>
                  <i className="ki-outline ki-setting-2 fs-6 me-sm-2 text-white" />
                  <span className="d-none d-sm-inline">Register Admin</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-people", label: "Total Users", value: stats.total, sub: "Registered users" },
                        { icon: "ki-shield-tick", label: "Security Personnel", value: stats.security, sub: "Security officers" },
                        { icon: "ki-setting-2", label: "Administrators", value: stats.admins, sub: "Admin accounts" },
                        { icon: "ki-element-11", label: "Phase Admins", value: stats.phaseAdmins, sub: "Phase administrators" },
                        // { icon: "ki-check-circle", label: "Active User Accounts", value: stats.active, sub: "Active accounts" },
                      ]}
                    />

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="System Users" subtitle="Search & manage user accounts" className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400" />
                          <input
                            className="d6m-input ps-10"
                            style={{ width: 280, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Search users by name, email or phone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>
                        <select className="d6m-input" style={{ width: 130, minHeight: 40, height: 40, borderRadius: 999 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                          <option value="all">All Roles</option>
                          <option value="Admin">Admin</option>
                          <option value="Security">Security</option>
                          <option value="Phase Admin">Phase Admin</option>
                        </select>
                        <select className="d6m-input" style={{ width: 130, minHeight: 40, height: 40, borderRadius: 999 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                        </select>
                        <div className="d6-seg">
                          <button type="button" className={`fs-8 fw-bold ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} title="List view">
                            <i className="ki-outline ki-row-horizontal fs-5 m-0" />
                          </button>
                          <button type="button" className={`fs-8 fw-bold ${viewMode === "card" ? "active" : ""}`} onClick={() => setViewMode("card")} title="Card view">
                            <i className="ki-outline ki-element-11 fs-5 m-0" />
                          </button>
                        </div>
                      </CardHead>

                      <div>
                        {isLoading ? (
                          <div className="text-center py-10">
                            <span className="spinner-border spinner-border-sm text-success me-2" />
                            Loading users...
                          </div>
                        ) : filtered.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-shield-cross fs-3x text-gray-300 d-block mb-3" />
                            <div className="d6-title text-gray-900 mb-1">No users found.</div>
                            <div className="fs-8 text-gray-500">Click "Register Admin" or "Register Security" to create accounts.</div>
                          </div>
                        ) : viewMode === "list" ? (
                          <div className="d-flex flex-column gap-3">
                            {pagedUsers.map((u) => (
                              <div
                                key={u.id}
                                className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 ${u.isActive ? "border-hover-success" : "border-hover-danger"} position-relative overflow-hidden cursor-pointer transition-all`}
                                style={{ minHeight: 90 }}
                                onClick={() => openEditModal(u)}
                              >
                                <div className={`position-absolute top-0 start-0 h-100 ${u.isActive ? "bg-success" : "bg-danger"} w-4px`} />

                                <div className="d-flex align-items-center me-4" style={{ width: "420px", flexShrink: 0 }}>
                                  <Avatar src={u.imageUrl} name={u.name} size={48} />
                                  <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                    <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                      <span className="fs-6 fw-bold text-gray-900 text-truncate">{u.name}</span>
                                      <span className={`badge ${u.role === "Admin" ? "bg-light-success text-success" : "bg-light-primary text-primary"} fs-9 fw-semibold rounded-pill px-2.5 py-0.5`}>
                                        {u.role}
                                      </span>
                                      {u.phoneNumber && (
                                        <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2 py-0.5">{u.phoneNumber}</span>
                                      )}
                                    </div>
                                    <div className="text-gray-500 fs-8 text-truncate">{u.email || "No email address"}</div>
                                  </div>
                                </div>

                                <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                                  {u.role === PHASE_ADMIN && (
                                    <div className="d-none d-sm-block text-center">
                                      <div className="text-gray-400 fs-9 mb-0.5">Phase</div>
                                      <div className="fw-semibold text-gray-900 fs-8">{u.phaseNames?.length ? u.phaseNames.join(", ") : "—"}</div>
                                    </div>
                                  )}
                                  <div className="d-none d-sm-block text-center">
                                    <div className="text-gray-400 fs-9 mb-0.5">Joined</div>
                                    <div className="fw-semibold text-gray-900 fs-8">{u.createdDateFmt}</div>
                                  </div>
                                  <div className="text-center">
                                    <StatusChip color={u.isActive ? "#16A34A" : "#DC2626"}>{u.isActive ? "Active" : "Disabled"}</StatusChip>
                                  </div>
                                  <span className="d6-icon-btn" title="Edit account"><i className="ki-outline ki-pencil fs-5" /></span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="row g-5">
                            {pagedUsers.map((u) => (
                              <div className="col-12 col-md-6 col-xl-4" key={u.id}>
                                <div
                                  className={`d6-card h-100 d-flex flex-column p-5 ${u.isActive ? "border-hover-success" : "border-hover-danger"} cursor-pointer transition-all`}
                                  onClick={() => openEditModal(u)}
                                >
                                  <div className="d-flex align-items-start justify-content-between gap-2 mb-4">
                                    <Avatar src={u.imageUrl} name={u.name} size={56} />
                                    <StatusChip color={u.isActive ? "#16A34A" : "#DC2626"}>{u.isActive ? "Active" : "Disabled"}</StatusChip>
                                  </div>
                                  <h4 className="d6-title text-gray-900 mb-1 text-truncate">{u.name}</h4>
                                  <span className="d6m-muted fs-8 text-truncate mb-4">{u.email || "No email"}</span>
                                  <div className={`d6m-kv ${u.role === PHASE_ADMIN ? "d6m-kv-3 " : ""}mt-auto`}>
                                    <div className="d6m-kv-cell">
                                      <span className="d6m-k">Role</span>
                                      <span className={`d6m-v ${u.role === "Admin" ? "text-success" : ""}`}>{u.role}</span>
                                    </div>
                                    {u.role === PHASE_ADMIN && (
                                      <div className="d6m-kv-cell">
                                        <span className="d6m-k">Phase</span>
                                        <span className="d6m-v text-truncate d-block">{u.phaseNames?.length ? u.phaseNames.join(", ") : "—"}</span>
                                      </div>
                                    )}
                                    <div className="d6m-kv-cell">
                                      <span className="d6m-k">Joined</span>
                                      <span className="d6m-v">{u.createdDateFmt}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {!isLoading && (
                          <Pagination
                            page={page}
                            totalPages={totalPages}
                            onChange={setPage}
                            summary={`Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} users`}
                          />
                        )}
                      </div>
                    </div>

                    <div className="my-10" />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-4">
                  <div className="text-gray-900 text-center fs-7">
                    <span className="text-gray-600 fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-600 text-hover-primary fw-bold">
                      Powered by Pixels Solutions Limited
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up" />
          </div>
        </div>
      </div>

      <AddUserModal modalRef={addModalRef} estateId={estateId} adminId={adminId} roleTarget={addRoleTarget} phases={phases} onClose={() => {}} onSuccess={fetchUsers} />
      <EditUserModal modalRef={editModalRef} user={selectedUser} estateId={estateId} adminId={adminId} phases={phases} onSuccess={fetchUsers} />
    </>
  );
}